import 'Components/vm-label/vm-label';
import 'Components/vm-link-overlay/vm-link-overlay';
import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { TemplateVersion } from 'Utils/templates';
import { formatDateRelative, formatViewCount, pluralize } from 'Utils/text';
import './vm-video-card-2025-12-1-long';
import './vm-video-card-2025-12-1-short';
import Styles from './vm-video-card.scss';

export interface Contributor {
    name: string;
    href?: string;
}

/**
 * Generic video-card layout.
 *
 * Renders a thumbnail (with optional duration overlay) next to a column
 * of metadata. All textual metadata is filled via named slots — the host
 * controls content, this component owns the visual layout, link wrapping,
 * and the placeholder/duration affordances.
 *
 * Stacking order matches `vm-kol-profile-video-card`: heading first,
 * then byline (publisher + optional badges + optional contributors),
 * then trailing meta. Consumers that need separators (e.g. " • ") between
 * publisher and contributors render them inside the slot content; empty
 * slots collapse via the default `slot { display: contents }` behaviour.
 *
 * @attr href            - Link destination. Covers the entire card via the
 *                          `vm-link-overlay` `link-overlay` slot, so secondary
 *                          links (publisher, contributors) stay independently
 *                          clickable instead of being nested inside it.
 * @attr heading         - Visible heading text (rendered as h2). Named `heading`
 *                          rather than `title` to avoid shadowing the global
 *                          HTML `title` (tooltip) attribute.
 * @attr thumbnail-src   - Thumbnail image URL. If empty, a placeholder is shown.
 * @attr duration-label  - Pre-formatted duration (e.g. "12:34") shown over the
 *                          thumbnail. Component does not format; caller decides.
 * @attr views-count     - Raw view count. Formatted compactly (e.g. 3420 → "3.4k").
 *                          Drives the default meta line so all consumers render the
 *                          same string; pass an explicit `meta` slot to override.
 * @attr created-at      - ISO-8601 publish timestamp. Formatted day-relative
 *                          (e.g. "3 days ago", "Nov 11, 2024"). Same source-of-truth
 *                          guarantee as `views-count`.
 * @attr from-industry   - Industry-sponsored card (e.g. a featured campaign). Replaces
 *                          the whole default meta line (views/date) with the fixed
 *                          "Information from Industry" disclosure label.
 * @attr publisher       - JSON `{name, href?}` object. Rendered as plain text or as
 *                          a link when `href` is present. Drives the default
 *                          publisher slot fallback so all consumers produce the
 *                          same markup; pass an explicit `publisher` slot to override.
 * @attr contributors    - JSON array of `{name, href?}` items. Rendered after the
 *                          publisher as ` • Name1 • Name2`, each name linked when
 *                          `href` is present. Pass an explicit `contributors` slot
 *                          to override.
 * @attr thumbs-up-count - Raw thumbs-up (like) count. Forwarded to the
 *                          `2025-12-1` long layout; ignored by the legacy layout.
 * @attr tags            - JSON array of strings rendered as `vm-label variant="primary"`
 *                          chips between the heading and the byline. Independent of `variant`; consumers
 *                          opt in by providing a non-empty list.
 * @attr variant         - Visual treatment. `short` matches the original KOL
 *                          profile sizing; `long` widens the thumbnail, enlarges
 *                          the heading, clamps it to 4 lines, and adds a bottom
 *                          separator for use in a stacked results list.
 * @attr target          - Anchor `target` for the overlay link.
 * @attr template-version - Markup template to render. `Latest` keeps the legacy
 *                          layout; `2025-12-1` delegates to the
 *                          `vm-video-card-2025-12-1-short`/`-long` components
 *                          (the `long` variant selects the long layout).
 * @slot publisher    - Override for the default publisher rendering. Leave empty
 *                       to use the formatted output of the `publisher` attribute.
 * @slot contributors - Override for the default contributors rendering. Leave empty
 *                       to use the formatted output of `contributors`.
 * @slot meta         - Override for the default views/date meta line. Leave empty
 *                       to use the formatted output of `views-count` + `created-at`
 *                       (or the `from-industry` disclosure label).
 *
 */
@customElement('vm-video-card')
export class VmVideoCard extends LitElement {
    static override styles = Styles;

    @property({ type: String }) href = '';
    @property({ type: String }) heading = '';
    @property({ type: String, attribute: 'thumbnail-src' }) thumbnailSrc = '';
    @property({ type: String, attribute: 'duration-label' }) durationLabel = '';
    @property({ type: Number, attribute: 'views-count' }) viewsCount = 0;
    @property({ type: String, attribute: 'created-at' }) createdAt = '';
    @property({ type: Boolean, attribute: 'from-industry' }) fromIndustry = false;
    @property({ type: Object }) publisher: Contributor | null = null;
    @property({ type: Array }) contributors: Array<Contributor> = [];
    @property({ type: Number, attribute: 'thumbs-up-count' }) thumbsUpCount = 0;
    @property({ type: Array }) tags: string[] = [];
    @property({ type: String, reflect: true }) variant: 'short' | 'long' = 'short';
    @property({ type: String }) target: HTMLAnchorElement['target'] = '';
    @property({ type: String, attribute: 'template-version' })
    templateVersion: TemplateVersion = TemplateVersion.Latest;

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-video-card');
        this.dataset.testid = 'vm-video-card';
    }

    private _renderDefaultPublisher() {
        if (!this.publisher?.name) return nothing;
        const { name, href } = this.publisher;
        return href ? html`<a href=${href}>${name}</a>` : html`${name}`;
    }

    private _renderTags() {
        if (!this.tags.length) return nothing;
        return html`
            <ul class="video-card__tags">
                ${this.tags.map((tag) => html`<li><vm-label variant="primary">${tag}</vm-label></li>`)}
            </ul>
        `;
    }

    private _renderDefaultContributors() {
        if (!this.contributors.length) return nothing;
        // Leading " • " separates the contributors from the publisher slot, matching
        // the `vm-kol-profile-video-card` byline format. Each contributor is rendered
        // as a link when an `href` is supplied, otherwise plain text.
        return html`
            •
            ${this.contributors.map((contributor, i) => {
                const separator = i < this.contributors.length - 1 ? ' • ' : '';
                return contributor.href
                    ? html`<a href=${contributor.href}>${contributor.name}</a>${separator}`
                    : html`<span>${contributor.name}${separator}</span>`;
            })}
        `;
    }

    private _renderDefaultMeta() {
        // The industry disclosure takes the meta line's place, so a sponsored card
        // never shows views/date alongside it.
        if (this.fromIndustry) {
            return html`<span class="video-card__industry-label">Information from Industry</span>`;
        }
        const parts: string[] = [];
        if (this.createdAt) parts.push(formatDateRelative(this.createdAt));
        if (this.viewsCount > 0) parts.push(`${formatViewCount(this.viewsCount)} view${pluralize(this.viewsCount)}`);
        return parts.length ? parts.join(' · ') : nothing;
    }

    private _renderThumbnail() {
        if (this.thumbnailSrc) {
            return html`<img src=${this.thumbnailSrc} alt="" class="video-card__img" />`;
        }
        return html`
            <div class="video-card__img video-card__img--placeholder" aria-hidden="true">
                <span>VIDEO</span>
            </div>
        `;
    }

    override render() {
        if (this.templateVersion === TemplateVersion.V2025_12_1) {
            // The `long` variant uses the wider "media" (long) layout; every
            // other variant uses the "related" (short) layout.
            if (this.variant === 'long') {
                return html`
                    <vm-video-card-2025-12-1-long
                        href=${this.href}
                        heading=${this.heading}
                        thumbnail-src=${this.thumbnailSrc}
                        duration-label=${this.durationLabel}
                        views-count=${this.viewsCount}
                        created-at=${this.createdAt}
                        thumbs-up-count=${this.thumbsUpCount}
                        target=${this.target}
                        .publisher=${this.publisher}
                        .contributors=${this.contributors}
                    ></vm-video-card-2025-12-1-long>
                `;
            }
            return html`
                <vm-video-card-2025-12-1-short
                    href=${this.href}
                    heading=${this.heading}
                    thumbnail-src=${this.thumbnailSrc}
                    duration-label=${this.durationLabel}
                    target=${this.target}
                    .publisher=${this.publisher}
                    .contributors=${this.contributors}
                ></vm-video-card-2025-12-1-short>
            `;
        }

        return html`
            <vm-link-overlay class="video-card">
                <div class="video-card__img-container">
                    ${this._renderThumbnail()}
                    ${this.durationLabel
                        ? html`<span class="video-card__duration">${this.durationLabel}</span>`
                        : nothing}
                </div>
                <div class="video-card__info">
                    <h4 class="video-card__title">${this.heading}</h4>
                    ${this._renderTags()}
                    <div class="video-card__details">
                        <div class="video-card__byline">
                            <slot name="publisher">${this._renderDefaultPublisher()}</slot>
                            <slot name="contributors">${this._renderDefaultContributors()}</slot>
                        </div>
                        <div class="video-card__meta">
                            <slot name="meta">${this._renderDefaultMeta()}</slot>
                        </div>
                    </div>
                </div>
                <a
                    slot="link-overlay"
                    href=${this.href}
                    target=${this.target || nothing}
                    class="video-card__link"
                    aria-label=${this.heading}
                ></a>
            </vm-link-overlay>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-card': VmVideoCard;
    }
}
